import React, { PropsWithChildren } from 'react'
import { StyledMaterialDesignContent } from './NotistackSnackbarKeyProvider'
import { SnackbarProvider as NotiSnackbarProvider } from 'notistack'
import { createMemoryRouter } from 'react-router'
import { createBrowserRouter } from 'react-router-dom'
import { AppRenderOptions, TestWrapperProps } from '../../testRenderer'
import {
  createPrivateProviders,
  createPublicProviders,
  createRouterProviders,
  withMockComponent,
} from './utils/withMockComponent'
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material'
import { globalStyles, muiTheme } from '../../stylesheet'
import CookieBanner from '../CookieBanner'
import ContextResetter from './ContextResetter'
import ScrollToTop from '../Routes/ScrollToTop'
import PrivateRoute from '../Routes/PrivateRoute'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

const env = process.env.NODE_ENV

export const PublicProviders: React.FC<
  PropsWithChildren & AppRenderOptions
> = ({
  children,
  loadingProviderConfig,
  countryConfig,
  inviteConfig,
  shouldMockInvite,
}) => {
  const isTest = env === 'test'
  const { Providers, LoadingProvider } = createPublicProviders({
    loadingProviderConfig,
    countryConfig,
    inviteConfig,
    shouldMockInvite,
  })
  return (
    <ThemeProvider theme={muiTheme}>
      <LoadingProvider>
        <Providers>
          {!isTest && <CssBaseline />}
          <>{children}</>
          {!isTest && <CookieBanner />}
        </Providers>
      </LoadingProvider>
    </ThemeProvider>
  )
}

export const PrivateProviders: React.FC<
  PropsWithChildren & AppRenderOptions
> = ({
  children,
  speedDialMenuConfig,
  shouldMockSpeedDial,
  accountConfig,
  programConfig,
  transcriptProviderConfig,
  eventProviderConfig,
  loadingProviderConfig,
  playlistProviderConfig,
  shouldMockLoading,
  shouldMockScroll,
  userProviderInitialUser,
  userConfig,
  dashboardProviderConfig,
  countryConfig,
  titleConfig,
  shouldMockTranscript,
  shouldMockEvent,
  shouldMockPlaylist,
  shouldMockUserContext,
  shouldMockUserAccountContext,
  notificationConfig,
  shouldMockSnackbarConfig,
}) => {
  const isTest = env === 'test'
  const ScrollToTopWrapper = withMockComponent({
    WrappedComponent: ScrollToTop,
    useMock: isTest,
  })
  const PrivateRouteWrapper = withMockComponent({
    /**
     * privateRoute is a component that returns the country information and login, currently we use
     * loggedInWithPermissions* to initiate the login information to the auth context,
     * returning this component in test environments causes tests to fail because we do not add this data and the privateRoute has its own test.
     * if we are not in testing, we are in development or production.
     */
    WrappedComponent: !isTest ? PrivateRoute : undefined,
    useMock: isTest,
  })
  const ContextResetterWrapper = withMockComponent({
    WrappedComponent: ContextResetter,
    useMock: isTest,
  })

  const { ScrollProvider, LoadingProvider, Providers } = createPrivateProviders(
    {
      speedDialMenuConfig,
      shouldMockSpeedDial,
      accountConfig,
      programConfig,
      transcriptProviderConfig,
      eventProviderConfig,
      loadingProviderConfig,
      shouldMockLoading,
      shouldMockScroll,
      userProviderInitialUser,
      userConfig,
      dashboardProviderConfig,
      countryConfig,
      titleConfig,
      playlistProviderConfig,
      shouldMockTranscript,
      shouldMockSnackbarConfig,
      shouldMockEvent,
      shouldMockPlaylist,
      shouldMockUserContext,
      shouldMockUserAccountContext,
      notificationConfig,
    }
  )

  return (
    <ScrollProvider>
      <ScrollToTopWrapper />
      <ThemeProvider theme={muiTheme}>
        {!isTest && <GlobalStyles styles={globalStyles} />}
        {!isTest && <CssBaseline />}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <LoadingProvider>
            <PrivateRouteWrapper>
              <Providers>
                <ContextResetterWrapper />
                {children}
              </Providers>
            </PrivateRouteWrapper>
          </LoadingProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ScrollProvider>
  )
}

export const testRouterType = createMemoryRouter([{}])
export const browserRouterType = createBrowserRouter([{}])

export const RouterProviders: React.FC<
  PropsWithChildren & {
    router: typeof testRouterType | typeof browserRouterType
  } & TestWrapperProps
> = ({
  children,
  router,
  auth,
  notiSnackbarProviderConfig,
  notistackSnackbarKeyConfig,
  shouldMockNotistackSnackbarKey,
  snackbarConfig,
  learningConfig,
  businessConfig,
  communitiesConfig,
  filterConfig,
  shouldMockFilter,
  shouldMockSnackbarConfig,
  shouldMockBusiness,
  shouldMockCommunities,
}) => {
  const Providers = createRouterProviders(
    {
      auth,
      notistackSnackbarKeyConfig,
      shouldMockNotistackSnackbarKey,
      shouldMockSnackbarConfig,
      snackbarConfig,
      learningConfig,
      businessConfig,
      communitiesConfig,
      filterConfig,
      shouldMockFilter,
      shouldMockBusiness,
      shouldMockCommunities,
    },
    router
  )

  return (
    <ThemeProvider theme={muiTheme}>
      <NotiSnackbarProvider
        Components={{
          success: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
        }}
        maxSnack={50}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={notiSnackbarProviderConfig?.onClose}
      >
        <Providers>{children}</Providers>
      </NotiSnackbarProvider>
    </ThemeProvider>
  )
}

export default {
  PrivateProviders,
  PublicProviders,
  RouterProviders,
}
